import Api from 'entities/helpers/api';
import EverTrue from 'app';
import Decorator from '@evertrue/client-decorator';
import { getDateInTimezone } from 'base/new-utils';
import { fromSnakeToTitleCase } from '@evertrue/sdk';

class ProposalFormController {
  static amountFields = ['expected', 'original_ask', 'ask', 'funded'];

  static submit = async (values, id) => {
    try {
      const { primary_contact, custom_field, ...restOfProposal } = values;

      // First, map the "custom_field" object into an array of objects with the key of "custom_fields"
      // also, remove the "primary_contact" and add it to the "contacts" array with type "PRIMARY"
      const formattedProposal = {
        ...restOfProposal,
        custom_fields: Object.entries(custom_field)
          .filter(([key, value]) => value !== undefined && value !== null && value !== '') // remove custom fields with no value from the payload. NOTE: false and 0 are both valid values.
          .map(([key, value]) => ({
            custom_field_property_id: key,
            value,
          })), // map to the API format
        contacts: [
          { contact_id: primary_contact.id, type: 'PRIMARY' },
          ...values.contacts.map((contact) => ({
            contact_id: contact.id,
            type: 'SECONDARY',
          })),
        ],
      };

      // update if id exists, otherwise create a new proposal
      if (id) {
        const updatedProposal = await Api.UGC.PROPOSAL.put({
          urlExtend: `/${id}`,
          urlArgs: { proposal_id: id },
          data: JSON.stringify(formattedProposal),
          success_message: 'Proposal updated successfully',
          success: () => {
            EverTrue.track.set('proposal_action', { type: 'edit_proposal' });
          },
        });
        return updatedProposal;
      } else {
        const newProposal = await Api.UGC.PROPOSAL.post({
          data: JSON.stringify(formattedProposal),
          success_message: 'Proposal created successfully',
          success: () => {
            EverTrue.track.set('proposal_action', { type: 'create_proposal' });
          },
        });

        return newProposal;
      }
    } catch {}
  };

  static fetchRules = async () => {
    try {
      const triggers = await Api.UGC.FIELD_TRIGGERS.get();
      const rules = triggers?.rules || [];
      // Filter out rules that are not related to proposals
      return rules.filter((rule) => rule.fields.some((field) => field.field_name.startsWith('proposal')));
    } catch (error) {
      console.error('Error fetching Field Trigger rules:', error);
      return [];
    }
  };

  /**
   * This method maps the standard fields configuration defined in DNA into the new FieldTrigger rules format.
   * @param {*} standardFieldsConfig -- the DNA Config Object for standard fields, which may include "required" and "max_length" properties.
   * @returns an array of rules that can be used in the FieldTrigger system to validate the standard fields.
   */
  static mapDnaToFieldTriggerRules = (standardFieldsConfig = {}) => {
    const configMap = Object.entries(standardFieldsConfig || {})
      .map(([key, configObject = {}]) => {
        const isRequired = configObject?.required || false;
        if (isRequired) {
          return {
            name: `DNA Standard Field Rule: ${key}`,
            rule_order: Number.MAX_SAFE_INTEGER, // Ensure these rules are at the end (highest priority)
            description: `${fromSnakeToTitleCase(key)} is required, per your organization's DNA configuration.`,
            condition: {
              if: [true, 'required'],
            },
            fields: [
              {
                field_name: `proposal.${key}`,
              },
            ],
          };
        } else return null;
      })
      .filter((field) => field !== null);

    // Other Rules
    configMap.push({
      name: 'DNA Standard Field Rule: Reported',
      rule_order: Number.MAX_SAFE_INTEGER, // Ensure this rule is at the end (highest priority)
      description: 'Proposal Reported Gate is enabled for this organization.',
      condition: {
        if: [
          {
            '!': [{ var: 'proposal.proposal_reported_gate_enabled' }],
          },
          'hidden',
          'visible',
        ],
      },
      fields: [
        {
          field_name: 'proposal.reported',
        },
      ],
    });

    return configMap;
  };

  /**
   * maps over custom fields configured for the organization and returns an array of rules that can be used in the FieldTrigger system to validate custom fields.
   * Each rule will have a name, description, condition, rule_order, and fields.
   * @param {boolean} hasCustomFields -- whether the organization has custom fields configured
   * @param {Array} orgCustomFields -- an array of custom fields configured for the organization, each field should have a "displayName" and "id" property.
   * @returns Array of rules that can be used in the FieldTrigger system to validate custom fields.
   */
  static mapCustomFieldsToRules = (hasCustomFields, orgCustomFields = []) => {
    if (!hasCustomFields || !orgCustomFields || orgCustomFields.length === 0) {
      return [];
    }

    return orgCustomFields
      .map((field) => {
        const { displayName, id, required } = field || {};
        if (required) {
          const fieldName = `proposal.custom_field.${id}`;
          return {
            name: `Custom Field Rule: ${displayName}`,
            description: `${displayName} is required, per your organization's custom fields configuration.`,
            condition: {
              if: [true, 'required'],
            },
            rule_order: Number.MIN_SAFE_INTEGER, // Ensure these rules are "before" the DNA rules (lowest priority)
            fields: [
              {
                field_name: fieldName,
              },
            ],
          };
        } else return null;
      })
      .filter((field) => field !== null);
  };

  static getAdditionalRules = (additionalContactLimit, solicitorLimit, designationLimit, standardFieldsConfig) => {
    // Grab the "max_length" rules from the standardFieldsConfig
    const maxLengthConfig = Object.entries(standardFieldsConfig || {}).reduce((acc, [key, value]) => {
      if (value?.max_length) {
        acc[key] = {
          max_length: value.max_length,
          message: `${fromSnakeToTitleCase(key)} must have fewer than ${
            value.max_length
          } characters, per your organization's DNA configuration.`,
        };
      }
      return acc;
    }, {});

    // Add additional rules for contacts, solicitors, and designations if limits are provided
    if (additionalContactLimit) {
      maxLengthConfig.contacts = {
        max_length: additionalContactLimit,
        message: `Must have fewer than ${additionalContactLimit} additional constituents, per your organization's DNA configuration.`,
      };
    }

    if (solicitorLimit) {
      maxLengthConfig.solicitors = {
        max_length: solicitorLimit,
        message: `Must have fewer than ${solicitorLimit} solicitors, per your organization's DNA configuration.`,
      };
    }

    if (designationLimit) {
      maxLengthConfig.proposal_designations = {
        max_length: designationLimit,
        message: `Must have fewer than ${designationLimit} designations, per your organization's DNA configuration.`,
      };
    }
    // Return the combined rules
    return maxLengthConfig;
  };

  static fetchParsedContacts = async (ids) => {
    if (!ids || !Array.isArray(ids) || ids.length === 0) return [];

    const contacts = (await Api.CONTACTS.SEARCH.post({
      data: JSON.stringify({ must: [{ id: { in: ids } }] }),
      params: { limit: 250 },
    })) || { items: [] };

    return (contacts?.items || []).map((contact) => {
      const parsedContact = Decorator.Contacts.parse(contact);
      return { ...parsedContact, name: Decorator.Contacts.getFullName(parsedContact) };
    });
  };

  static parseProposal = (proposal) => {
    const { custom_fields = [], ...rest } = proposal;
    // Ensure custom fields are in the correct format
    if (Array.isArray(custom_fields)) {
      rest.custom_field = custom_fields.reduce((acc, field) => {
        let value;
        switch (field.data_type) {
          case 'DATE': // it's just a timestamp
          case 'LONG':
          case 'DOUBLE':
          case 'NUMBER': {
            value = Number(field.value) || null;
            break;
          }
          case 'BOOLEAN': {
            value = field.value === 'true' || field.value === true;
            break;
          }
          default: {
            value = field.value;
          }
        }
        acc[field.custom_field_property_id] = value;
        return acc;
      }, {});
    } else {
      rest.custom_field = {};
    }

    return {
      ...rest,
      contacts: [], // set in useEffect in ProposalFormBody
      primary_contact: {}, // set in useEffect in ProposalFormBody
    };
  };

  static standardFieldsWithCustomFields = (hasCustomFields, orgCustomFields) => {
    const standardFields = Object.keys(this.defaultValues());
    if (hasCustomFields && orgCustomFields && orgCustomFields.length > 0) {
      const customFieldNames = orgCustomFields.map((field) => `custom_field.${field.id}`);
      return [...standardFields, ...customFieldNames];
    }
    return standardFields;
  };

  static defaultValues = (hasCustomFields, customFields) => {
    const now = getDateInTimezone().getTime();
    return {
      active: true,
      primary_contact: {},
      solicitors: [],
      contacts: [],
      created_date: now,
      title: '',
      description: '',
      status: '',
      original_ask_amount: '',
      original_ask_date: null,
      ask_amount: '',
      ask_date: null,
      expected_amount: '',
      expected_date: null,
      funded_amount: '',
      funded_date: null,
      custom_field: {}, // NOTE: "custom_field" is used here, but it needs to be "custom_fields" in the API.
      proposal_type: '',
      reported: false,
      confidence_score: undefined,
      proposal_designations: [],
      primary_proposal_unit: '',
      additional_proposal_category: '',
    };

    /**
     * Note: the following fields exist on the API but are derived there and are not input by users:
     * status_start_date // set to current date in the API
     * deleted // boolean field, set to false in the API
     * creator_user_id // set to the current user in the API
     * updating_user_id // set to the current user in the API
     * labels // ??
     * created_at // set to current date in the API
     * updated_at // set to current date in the API
     * update_source // set to an enum in the API depending on source (e.g. a user action or an import)
     * creation_source // set to an enum in the API depending on source (e.g. a user action or an import)
     */
  };
}

export default ProposalFormController;
